'use client';

import { c as _c } from "react/compiler-runtime";
import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import Banner from '@precis-digital/kurama/Banner';
import Grid from '@precis-digital/kurama/Grid';
import Typography from '@precis-digital/kurama/Typography';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkIfReportDataKnownErrorViewConditionSatisfied, checkIfReportDataLoadingViewConditionSatisfied, checkIfReportDataUnknownErrorViewConditionSatisfied } from 'reportingSolution/utils';
import { type ReportType } from 'shared/api/analytics';
import Block from 'shared/components/Block';
import { type ConversionEventsFlyoutProps, type EventOption } from 'shared/components/ConversionEventsFlyout';
import NoData from 'shared/components/Error/NoData';
import { type ActiveFilter } from 'shared/components/Filter';
import { useQueryString } from 'shared/components/Router';
import { type ConfigStatus, type ErrorCodeType, type ErrorDetails, STATUSES, getErrorCodeDetails, getStatusFromAPIStatusData } from 'shared/configStatuses';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { getPeriodOfTimeToday, getPreviousTimePeriod, getPreviousYearTimePeriod, format, startOfToday, startOfYesterday, subMonths } from 'shared/dateFns';
import useDagView from 'shared/hooks/useDagView';
import { useQueryParameterState } from 'shared/hooks/useQueryParameterState';
import { LOCAL_STORAGE_KEYS, get as getFromStore, store as saveToStore } from 'shared/lstore';
import OverScreen from 'shared/overScreens/niceModalReact';
import { APP_ROUTES } from 'shared/routes';
import { useTranslation } from 'shared/translations';
import { convertSnakeCaseToCamelCase, objectKeys } from 'shared/utils';
import { useMutationStandardReportStatuses, useQueryStandardReports } from 'src/home/api';
import { isBudgetMetric } from 'src/home/budgetsAndTargets';
import { CONVERSION_EVENTS_DEFAULTS, CONVERSION_SOURCE_TO_DATASOURCE_MAP, REPORT_TEMPLATE_WITH_CONVERSION_EVENTS, STATUSES_TO_SHOW_BANNER_FOR } from 'src/home/constants';
import { useHomePageData } from 'src/home/hooks/useHomePageData';
import { type Metric, reportsTypeParametersMap } from 'src/home/reportTemplateParameters';
import { type ChannelGroupingMetric } from 'src/home/reportTemplateParameters/channelGrouping';
import { type ReportingBaseMetric } from 'src/home/reportTemplateParameters/reportingBase';
import { type Filters, type Report, type TransformToDetailedDataFn, type TransformToTimeSeriesByMetricAndBreakdownFn, transformToFilters } from 'src/home/transformations';
import { type ReportingBaseReport } from 'src/home/transformations/reportingBase';
import { z } from 'zod';
import ReportDataLoading from 'shared/components/ReportDataLoading';
import { NativePinterestBanner } from 'shared/components/promoBanners/nativePinterestBanner';
import { type BudgetAndTargetsFlyoutProps } from './BudgetAndTargetsFlyout';
import DetailedReport from './DetailedReport';
import Layout from './Layout';
import NoReport from './NoReport';
import ProgressOverTime from './ProgressOvertime';
import RecommendationsV2 from './RecommendationsV2';
import ReportDataError from './ReportDataError';
import Summary from './Summary';
import { type ReportConfig } from './SwitchReportFlyout';
import Toolbar from './Toolbar';
import { type TopCardMetricsFlyoutProps } from './TopCardMetricsFlyout';
import { type AllowedStandardReport, checkIfCustomContentConditionSatisfied, checkIfFilterConditionIsSatisfied, checkIfNoReportDataViewConditionSatisfied, checkIfReportConfigAndStatusLoaded, checkIfReportTemplateWithNoConversionEvents, checkIfToolbarConditionSatisfied, checkIfValidStatusForShowingHomePageData, getDefaultConversionEvents, getSelectedStandardReportConfig, parseStoredCustomComparisonRange, persistHomeComparisonPeriodSelection, persistHomeCustomComparisonRange, transformFiltersToAPIFormat, type HomePageFilterParameter, type TimePeriodType, HOME_PAGE_FILTER_FIELD_NAMES } from './utils';
import { useHomeCopilotTools } from 'src/home/copilot/tools';
import { useHomeCopilotElementRefs } from 'src/home/hooks/useHomeCopilotElementRefs';
import { useHomeCopilotScorecardElementRefs } from 'src/home/hooks/useHomeCopilotScorecardElementRefs';
import { useHomeDetailedTableQueryState } from 'src/home/hooks/useHomeDetailedTableQueryState';
import { useHomeProgressOverTimeChartQueryState } from 'src/home/hooks/useHomeProgressOverTimeChartQueryState';
const defaultFilters: Filters = {
  markets: new Set(),
  channels: new Set()
};
const Dashboard = () => {
  const $ = _c(294);
  const theme = useTheme();
  const {
    t
  } = useTranslation("home");
  const {
    t: tCommon
  } = useTranslation("common");
  const {
    query: t0,
    pushQuery
  } = useQueryString();
  const {
    reportId: queryReportId,
    clientId: queryClientId
  } = t0;
  const {
    currentClient
  } = useCurrentClient();
  const {
    currentUser
  } = useAuth();
  const [userSelectedPeriodType, setUserSelectedPeriodType] = useState("previous_period");
  const userSelectedPeriodTypeRef = useRef("previous_period");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = periodType => {
      userSelectedPeriodTypeRef.current = periodType;
      setUserSelectedPeriodType(periodType);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const changeUserSelectedPeriodType = t1;
  const {
    dateRangeCalendarRef,
    filterBarRef,
    scorecardGroupRef,
    progressOverTimeChartRef,
    detailedTableRef,
    recommendationsRef,
    moreMenuButtonRef
  } = useHomeCopilotElementRefs();
  const [periodTypeToApply, setPeriodTypeToApply] = useState(userSelectedPeriodType);
  const [customComparisonRange, setCustomComparisonRange] = useState(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = format(startOfYesterday(), "yyyy-MM-dd");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const defaultEndDate = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = format(subMonths(startOfToday(), 1), "yyyy-MM-dd");
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const defaultStartDate = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      startDate: defaultStartDate,
      endDate: defaultEndDate
    };
    t5 = {
      schema: z.object({
        startDate: z.string(),
        endDate: z.string()
      })
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  const [selectedDate, setSelectedDate] = useQueryParameterState("date", t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = z.array(z.object({
      parameter: z.object({
        value: z.enum(HOME_PAGE_FILTER_FIELD_NAMES),
        label: z.string()
      }),
      operator: z.string(),
      data: z.union([z.string(), z.object({
        value: z.string(),
        label: z.string()
      }).array(), z.object({
        value: z.string(),
        label: z.string()
      })])
    }));
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const activeFilterSchema = t6;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    t8 = {
      schema: activeFilterSchema
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  const [selectedFilters, setSelectedFilters] = useQueryParameterState("filters", t7, t8);
  const [eventOptions, setEventOptions] = useState();
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const [selectedTopCardMetrics, setSelectedTopCardMetrics] = useState(t9);
  const [selectedConversionEvents, setSelectedConversionEvents] = useState();
  const [hasExistingConversionEvent, setHasExistingConversionEvent] = useState(false);
  const [selectedStandardReportConfigId, setSelectedStandardReportConfigId] = useState(queryReportId as string);
  const [isBudgetMode, setIsBudgetMode] = useState(false);
  let t10;
  bb0: {
    if (periodTypeToApply === "custom" && customComparisonRange != null) {
      let t11;
      if ($[9] !== customComparisonRange.endDate || $[10] !== customComparisonRange.startDate) {
        t11 = [customComparisonRange.startDate, customComparisonRange.endDate];
        $[9] = customComparisonRange.endDate;
        $[10] = customComparisonRange.startDate;
        $[11] = t11;
      } else {
        t11 = $[11];
      }
      t10 = t11;
      break bb0;
    }
    let t11;
    if ($[12] !== periodTypeToApply || $[13] !== selectedDate.endDate || $[14] !== selectedDate.startDate) {
      t11 = periodTypeToApply === "previous_year" ? getPreviousYearTimePeriod(selectedDate.startDate, selectedDate.endDate) : getPreviousTimePeriod(selectedDate.startDate, selectedDate.endDate);
      $[12] = periodTypeToApply;
      $[13] = selectedDate.endDate;
      $[14] = selectedDate.startDate;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    t10 = t11;
  }
  const previousTimePeriod = t10;
  const {
    data: t11,
    isSuccess: hasSuccessfullyLoadedStandardReportsConfig
  } = useQueryStandardReports(currentClient.id);
  let t12;
  if ($[16] !== t11) {
    t12 = t11 === undefined ? [] : t11;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const standardReportsConfigs = t12;
  const {
    data: reportConfigStatuses,
    mutate: getStandardReportStatuses,
    isPending: isLoadingReportStatuses,
    status: getStandardReportStatusesStatus
  } = useMutationStandardReportStatuses();
  const getStandardReportStatusesRequestIsIdle = getStandardReportStatusesStatus === "idle";
  const {
    getHomePageData,
    resetHomePageData,
    reportDataAnalytics,
    previousDataAnalytics,
    totalsGroupedByDate,
    totalsData,
    previousTotalsData,
    isDataLoaded: isHomePageDataLoaded,
    isDataError,
    isDataNotFoundError,
    isFilterOptionsLoaded,
    filterOptionsData,
    isConversionNamesAndSourcesLoaded,
    isLoadingConversionNamesAndSources,
    getFilterOptions,
    getConversionNamesAndSources,
    conversionNameAndSources,
    isConversionNamesAndSourcesError
  } = useHomePageData();
  let t13;
  bb1: {
    if (standardReportsConfigs.length > 0) {
      let t14;
      if ($[18] !== standardReportsConfigs) {
        t14 = standardReportsConfigs.filter(_temp);
        $[18] = standardReportsConfigs;
        $[19] = t14;
      } else {
        t14 = $[19];
      }
      t13 = t14;
      break bb1;
    }
    let t14;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = [];
      $[20] = t14;
    } else {
      t14 = $[20];
    }
    t13 = t14;
  }
  const standardReportConfigsFilteredByTemplateId = t13;
  let t14;
  if ($[21] !== currentClient.id || $[22] !== selectedStandardReportConfigId) {
    t14 = selectedStandardReportConfigId ?? getFromStore(LOCAL_STORAGE_KEYS.SELECTED_REPORT_ID_FOR_CLIENT_HOMEPAGE(currentClient.id));
    $[21] = currentClient.id;
    $[22] = selectedStandardReportConfigId;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== standardReportConfigsFilteredByTemplateId || $[25] !== t14) {
    t15 = getSelectedStandardReportConfig(standardReportConfigsFilteredByTemplateId, t14);
    $[24] = standardReportConfigsFilteredByTemplateId;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const selectedStandardReportConfig = t15;
  let t16;
  if ($[27] !== getStandardReportStatusesRequestIsIdle || $[28] !== hasSuccessfullyLoadedStandardReportsConfig || $[29] !== isLoadingReportStatuses) {
    t16 = checkIfReportConfigAndStatusLoaded({
      hasSuccessfullyLoadedStandardReportsConfig,
      isLoadingReportStatuses,
      getStandardReportStatusesRequestIsIdle
    });
    $[27] = getStandardReportStatusesRequestIsIdle;
    $[28] = hasSuccessfullyLoadedStandardReportsConfig;
    $[29] = isLoadingReportStatuses;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const isReportConfigAndStatusLoaded = t16;
  let t17;
  let t18;
  if ($[31] !== hasSuccessfullyLoadedStandardReportsConfig || $[32] !== pushQuery || $[33] !== queryClientId || $[34] !== queryReportId || $[35] !== selectedStandardReportConfig) {
    t17 = () => {
      if (selectedStandardReportConfig != null && queryClientId === selectedStandardReportConfig.clientId.toString()) {
        if (queryReportId == null || queryReportId !== selectedStandardReportConfig.configId.toString()) {
          pushQuery({
            reportId: selectedStandardReportConfig.configId.toString()
          });
        } else {
          if (hasSuccessfullyLoadedStandardReportsConfig && selectedStandardReportConfig == null) {
            pushQuery({}, true);
          }
        }
      }
    };
    t18 = [hasSuccessfullyLoadedStandardReportsConfig, pushQuery, queryClientId, queryReportId, selectedStandardReportConfig];
    $[31] = hasSuccessfullyLoadedStandardReportsConfig;
    $[32] = pushQuery;
    $[33] = queryClientId;
    $[34] = queryReportId;
    $[35] = selectedStandardReportConfig;
    $[36] = t17;
    $[37] = t18;
  } else {
    t17 = $[36];
    t18 = $[37];
  }
  useEffect(t17, t18);
  let t19;
  bb2: {
    if (selectedStandardReportConfig != null) {
      t19 = reportsTypeParametersMap[selectedStandardReportConfig.templateId];
      break bb2;
    }
    t19 = undefined;
  }
  const reportParameters = t19;
  let t20;
  let t21;
  if ($[38] !== currentClient.id || $[39] !== getStandardReportStatuses) {
    t20 = () => {
      getStandardReportStatuses(currentClient.id);
    };
    t21 = [currentClient.id, getStandardReportStatuses];
    $[38] = currentClient.id;
    $[39] = getStandardReportStatuses;
    $[40] = t20;
    $[41] = t21;
  } else {
    t20 = $[40];
    t21 = $[41];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[42] !== currentClient.id || $[43] !== selectedStandardReportConfig) {
    t22 = () => {
      if (selectedStandardReportConfig != null) {
        saveToStore(LOCAL_STORAGE_KEYS.SELECTED_REPORT_ID_FOR_CLIENT_HOMEPAGE(currentClient.id), selectedStandardReportConfig.configId.toString());
        const periodType_0 = (getFromStore(LOCAL_STORAGE_KEYS.HOME_PAGE_COMPARISON_PERIOD_SELECTION(selectedStandardReportConfig.configId.toString())) ?? "previous_period") as TimePeriodType;
        const storedCustomRange = getFromStore(LOCAL_STORAGE_KEYS.HOME_PAGE_CUSTOM_COMPARISON_RANGE(selectedStandardReportConfig.configId.toString()));
        const parsedCustomRange = parseStoredCustomComparisonRange(storedCustomRange);
        const resolvedPeriodType = periodType_0 === "custom" && parsedCustomRange == null ? "previous_period" : periodType_0;
        setCustomComparisonRange(parsedCustomRange);
        changeUserSelectedPeriodType(resolvedPeriodType);
        setPeriodTypeToApply(resolvedPeriodType);
      }
    };
    t23 = [currentClient.id, selectedStandardReportConfig, changeUserSelectedPeriodType];
    $[42] = currentClient.id;
    $[43] = selectedStandardReportConfig;
    $[44] = t22;
    $[45] = t23;
  } else {
    t22 = $[44];
    t23 = $[45];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[46] !== currentClient.currency || $[47] !== currentClient.id || $[48] !== getFilterOptions || $[49] !== isBudgetMode || $[50] !== reportParameters || $[51] !== selectedStandardReportConfig) {
    t24 = () => {
      if (selectedStandardReportConfig != null && reportParameters != null) {
        getFilterOptions({
          clientId: currentClient.id,
          targetCurrencyCode: currentClient.currency,
          id: selectedStandardReportConfig.configId.toString(),
          report: selectedStandardReportConfig.templateId as ReportType,
          filters: reportParameters.customDataFilters ?? [],
          ...reportParameters.filterVariables
        });
        setHasExistingConversionEvent(REPORT_TEMPLATE_WITH_CONVERSION_EVENTS.includes(selectedStandardReportConfig.templateId) && reportParameters.getSelectedConversionEvents?.(selectedStandardReportConfig.configId.toString()) != null);
        const filteredMetrics = reportParameters.getSelectedTopCardMetrics().filter(metric => isBudgetMetric(metric) ? isBudgetMode : true);
        setSelectedTopCardMetrics(filteredMetrics);
        setIsBudgetMode(reportParameters.getFromLocalStorageIsBudgetMode(selectedStandardReportConfig.configId.toString()));
      }
    };
    t25 = [currentClient.currency, currentClient.id, getFilterOptions, reportParameters, selectedStandardReportConfig, isBudgetMode];
    $[46] = currentClient.currency;
    $[47] = currentClient.id;
    $[48] = getFilterOptions;
    $[49] = isBudgetMode;
    $[50] = reportParameters;
    $[51] = selectedStandardReportConfig;
    $[52] = t24;
    $[53] = t25;
  } else {
    t24 = $[52];
    t25 = $[53];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[54] !== currentClient.currency || $[55] !== currentClient.id || $[56] !== getConversionNamesAndSources || $[57] !== reportParameters || $[58] !== selectedDate.endDate || $[59] !== selectedDate.startDate || $[60] !== selectedStandardReportConfig) {
    t26 = () => {
      if (selectedStandardReportConfig != null && REPORT_TEMPLATE_WITH_CONVERSION_EVENTS.includes(selectedStandardReportConfig.templateId) && reportParameters != null && reportParameters.getSelectedConversionEvents?.(selectedStandardReportConfig.configId.toString()) == null) {
        getConversionNamesAndSources({
          clientId: currentClient.id,
          targetCurrencyCode: currentClient.currency,
          id: selectedStandardReportConfig.configId.toString(),
          report: selectedStandardReportConfig.templateId as ReportType,
          filters: [{
            field: "date",
            operator: "BETWEEN",
            value: [selectedDate.startDate, selectedDate.endDate]
          }],
          ...reportParameters.conversionEventVariables
        });
      }
    };
    t27 = [currentClient.currency, currentClient.id, getConversionNamesAndSources, reportParameters, selectedDate.endDate, selectedDate.startDate, selectedStandardReportConfig];
    $[54] = currentClient.currency;
    $[55] = currentClient.id;
    $[56] = getConversionNamesAndSources;
    $[57] = reportParameters;
    $[58] = selectedDate.endDate;
    $[59] = selectedDate.startDate;
    $[60] = selectedStandardReportConfig;
    $[61] = t26;
    $[62] = t27;
  } else {
    t26 = $[61];
    t27 = $[62];
  }
  useEffect(t26, t27);
  const isReportTemplateWithNoConversionEvents = checkIfReportTemplateWithNoConversionEvents(selectedStandardReportConfig);
  useEffect(() => {
    if (selectedStandardReportConfig != null && reportParameters != null && (isReportTemplateWithNoConversionEvents || selectedConversionEvents != null)) {
      getHomePageData({
        currentTimePeriod: [selectedDate.startDate, selectedDate.endDate],
        previousTimePeriod,
        clientId: currentClient.id,
        reportId: selectedStandardReportConfig.configId,
        currency: currentClient.currency,
        reportType: selectedStandardReportConfig.templateId,
        reportAnalyticsVariables: reportParameters.reportAnalyticsVariables,
        totalVariables: reportParameters.totalsVariables,
        customFilters: [...transformFiltersToAPIFormat({
          selectedMarketAndChannelFilters: selectedFilters,
          selectedConversionEvents
        }), ...(reportParameters.customDataFilters ?? [])]
      });
    }
  }, [currentClient.currency, currentClient.id, getHomePageData, isReportTemplateWithNoConversionEvents, previousTimePeriod, reportParameters, selectedConversionEvents, selectedDate.endDate, selectedDate.startDate, selectedFilters, selectedStandardReportConfig]);
  const isConversionNamesAndSourcesSuccessfullyLoaded = isConversionNamesAndSourcesLoaded;
  let t28;
  let t29;
  if ($[63] !== conversionNameAndSources || $[64] !== hasExistingConversionEvent || $[65] !== isConversionNamesAndSourcesSuccessfullyLoaded || $[66] !== reportParameters || $[67] !== selectedStandardReportConfig) {
    t28 = () => {
      if (selectedStandardReportConfig != null && REPORT_TEMPLATE_WITH_CONVERSION_EVENTS.includes(selectedStandardReportConfig.templateId) && (isConversionNamesAndSourcesSuccessfullyLoaded || hasExistingConversionEvent) && reportParameters != null) {
        const options = conversionNameAndSources?.map(_temp2);
        options != null && setEventOptions(options);
        const defaultConversionEvents = getDefaultConversionEvents(options, CONVERSION_EVENTS_DEFAULTS);
        const conversionEvents = reportParameters.getSelectedConversionEvents?.(selectedStandardReportConfig.configId.toString()) ?? defaultConversionEvents;
        setSelectedConversionEvents(conversionEvents);
        reportParameters.storeSelectedConversionEvents?.(selectedStandardReportConfig.configId.toString(), conversionEvents);
      }
    };
    t29 = [conversionNameAndSources, hasExistingConversionEvent, isConversionNamesAndSourcesSuccessfullyLoaded, reportParameters, selectedStandardReportConfig];
    $[63] = conversionNameAndSources;
    $[64] = hasExistingConversionEvent;
    $[65] = isConversionNamesAndSourcesSuccessfullyLoaded;
    $[66] = reportParameters;
    $[67] = selectedStandardReportConfig;
    $[68] = t28;
    $[69] = t29;
  } else {
    t28 = $[68];
    t29 = $[69];
  }
  useEffect(t28, t29);
  let t30;
  bb3: {
    if (reportDataAnalytics.length > 0 && reportParameters != null) {
      let t31;
      if ($[70] !== reportDataAnalytics || $[71] !== reportParameters.transformations) {
        t31 = reportParameters.transformations.transformToReport(reportDataAnalytics);
        $[70] = reportDataAnalytics;
        $[71] = reportParameters.transformations;
        $[72] = t31;
      } else {
        t31 = $[72];
      }
      t30 = t31;
      break bb3;
    }
    let t31;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = [];
      $[73] = t31;
    } else {
      t31 = $[73];
    }
    t30 = t31;
  }
  const reports = t30;
  let t31;
  bb4: {
    if (previousDataAnalytics.length > 0 && reportParameters != null) {
      let t32;
      if ($[74] !== previousDataAnalytics || $[75] !== reportParameters?.transformations) {
        t32 = reportParameters?.transformations.transformToReport(previousDataAnalytics);
        $[74] = previousDataAnalytics;
        $[75] = reportParameters?.transformations;
        $[76] = t32;
      } else {
        t32 = $[76];
      }
      t31 = t32;
      break bb4;
    }
    let t32;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = [];
      $[77] = t32;
    } else {
      t32 = $[77];
    }
    t31 = t32;
  }
  const reportsForPreviousTimePeriod = t31;
  let t32;
  bb5: {
    if (filterOptionsData.length > 0 && reportParameters != null) {
      let t33;
      if ($[78] !== filterOptionsData || $[79] !== reportParameters) {
        let t34;
        if ($[81] !== reportParameters) {
          t34 = filter => {
            const marketFieldVariableName = convertSnakeCaseToCamelCase(reportParameters.marketFieldVariableName) as keyof typeof filter;
            const channelFieldVariableName = convertSnakeCaseToCamelCase(reportParameters.channelFieldVariableName) as keyof typeof filter;
            return {
              market: filter[marketFieldVariableName] as string,
              channel: filter[channelFieldVariableName] as string
            };
          };
          $[81] = reportParameters;
          $[82] = t34;
        } else {
          t34 = $[82];
        }
        const mappedFilterOptions = filterOptionsData.map(t34);
        t33 = transformToFilters(mappedFilterOptions);
        $[78] = filterOptionsData;
        $[79] = reportParameters;
        $[80] = t33;
      } else {
        t33 = $[80];
      }
      t32 = t33;
      break bb5;
    }
    t32 = defaultFilters;
  }
  const allFilters = t32;
  let t33;
  if ($[83] !== isReportConfigAndStatusLoaded || $[84] !== reportConfigStatuses || $[85] !== selectedStandardReportConfig || $[86] !== tCommon) {
    bb6: {
      const statusInfo = {
        selectedReportStatus: undefined,
        selectedReportErrorCode: undefined,
        selectedReportErrorDetails: undefined,
        selectedReportStatusMessage: undefined,
        selectedReportStatusLastUpdated: undefined
      };
      if (selectedStandardReportConfig == null || !isReportConfigAndStatusLoaded) {
        t33 = statusInfo;
        break bb6;
      }
      const selectedReportConfigStatus = reportConfigStatuses?.find(status => status.configId === selectedStandardReportConfig.configId.toString());
      statusInfo.selectedReportStatus = getStatusFromAPIStatusData(selectedStandardReportConfig.runSchedule, selectedReportConfigStatus?.status);
      statusInfo.selectedReportStatusMessage = selectedReportConfigStatus?.message;
      statusInfo.selectedReportErrorCode = selectedReportConfigStatus?.errorCode;
      statusInfo.selectedReportErrorDetails = getErrorCodeDetails(selectedReportConfigStatus?.errorCode, selectedReportConfigStatus?.errorMessage, selectedReportConfigStatus?.targetProjectId, tCommon("dagView.reportingSolution"));
      statusInfo.selectedReportStatusLastUpdated = selectedReportConfigStatus?.lastUpdated;
      t33 = statusInfo;
    }
    $[83] = isReportConfigAndStatusLoaded;
    $[84] = reportConfigStatuses;
    $[85] = selectedStandardReportConfig;
    $[86] = tCommon;
    $[87] = t33;
  } else {
    t33 = $[87];
  }
  const {
    selectedReportStatus,
    selectedReportErrorCode,
    selectedReportErrorDetails,
    selectedReportStatusMessage,
    selectedReportStatusLastUpdated
  } = t33;
  const isValidStatusForShowingHomePageData = checkIfValidStatusForShowingHomePageData(selectedReportStatus);
  const {
    query
  } = useQueryString();
  const {
    showDagView,
    shouldShowDagView
  } = useDagView("reportingSolution", true);
  const shouldShowDagViewPopup = shouldShowDagView(query);
  if (selectedStandardReportConfig != null && shouldShowDagViewPopup && selectedReportStatus != null) {
    showDagView({
      configId: selectedStandardReportConfig.configId,
      configName: selectedStandardReportConfig.name,
      isActive: selectedStandardReportConfig.runSchedule,
      configStatus: selectedReportStatus
    });
  }
  const canFilter = checkIfFilterConditionIsSatisfied(isValidStatusForShowingHomePageData, filterOptionsData.length);
  allFilters?.channels;
  allFilters?.markets;
  let t34;
  if ($[88] !== allFilters?.channels || $[89] !== allFilters?.markets || $[90] !== reportParameters || $[91] !== t) {
    t34 = reportParameters != null ? [{
      value: reportParameters.marketFieldVariableName,
      label: t("filter.market"),
      options: Array.from(allFilters?.markets ?? []).map(_temp3)
    }, {
      value: reportParameters.channelFieldVariableName,
      label: t("filter.channel"),
      options: Array.from(allFilters?.channels ?? []).map(_temp4)
    }] : [];
    $[88] = allFilters?.channels;
    $[89] = allFilters?.markets;
    $[90] = reportParameters;
    $[91] = t;
    $[92] = t34;
  } else {
    t34 = $[92];
  }
  const parameterOptions = t34;
  const isConversionNameAndSourceConditionSatisfied = isConversionNamesAndSourcesSuccessfullyLoaded || hasExistingConversionEvent || isReportTemplateWithNoConversionEvents;
  const hasSuccessfullyLoadedNecessaryData = isReportConfigAndStatusLoaded && isHomePageDataLoaded && isConversionNameAndSourceConditionSatisfied;
  const isNoReportViewConditionSatisfied = selectedStandardReportConfig == null;
  const isReportDataLoadingViewConditionSatisfied = checkIfReportDataLoadingViewConditionSatisfied({
    status: selectedReportStatus,
    isDataNotFoundError
  });
  let t35;
  if ($[93] !== isDataNotFoundError || $[94] !== selectedReportStatus) {
    t35 = checkIfReportDataKnownErrorViewConditionSatisfied({
      status: selectedReportStatus,
      isDataNotFoundError
    });
    $[93] = isDataNotFoundError;
    $[94] = selectedReportStatus;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  const isReportDataKnownErrorViewConditionSatisfied = t35;
  const isReportDataUnknownErrorViewConditionSatisfied = checkIfReportDataUnknownErrorViewConditionSatisfied({
    status: selectedReportStatus,
    isDataError,
    isReportDataLoadingViewConditionSatisfied
  });
  const isNoReportDataViewConditionSatisfied = checkIfNoReportDataViewConditionSatisfied({
    hasSuccessfullyLoadedNecessaryData,
    isDataError,
    status: selectedReportStatus,
    dataAnalyticsCount: reportDataAnalytics.length
  });
  const isNonErrorViewConditionSatisfied = isNoReportViewConditionSatisfied || isReportDataLoadingViewConditionSatisfied || isNoReportDataViewConditionSatisfied;
  const isCustomContentConditionSatisfied = checkIfCustomContentConditionSatisfied({
    isReportConfigAndStatusLoaded,
    shouldShowErrorView: isReportDataKnownErrorViewConditionSatisfied || isReportDataUnknownErrorViewConditionSatisfied,
    shouldShowNoDataView: isNoReportDataViewConditionSatisfied,
    shouldShowCustomNonErrorView: isNonErrorViewConditionSatisfied
  });
  const isToolbarConditionSatisfied = checkIfToolbarConditionSatisfied({
    isCustomContentConditionSatisfied,
    isDataLoaded: isReportConfigAndStatusLoaded,
    isFilterOptionsLoaded,
    isConversionNameAndSourceConditionSatisfied
  });
  let t36;
  bb7: {
    if (reportParameters == null) {
      let t37;
      if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
        t37 = [];
        $[96] = t37;
      } else {
        t37 = $[96];
      }
      t36 = t37;
      break bb7;
    }
    if (isBudgetMode) {
      t36 = reportParameters.metrics as Metric[];
      break bb7;
    }
    let t37;
    if ($[97] !== reportParameters.metrics) {
      t37 = reportParameters.metrics.filter(_temp5);
      $[97] = reportParameters.metrics;
      $[98] = t37;
    } else {
      t37 = $[98];
    }
    t36 = t37 as Metric[];
  }
  const listOfMetricsToShowInDropDownWithBudgetMetrics = t36;
  let t37;
  if ($[99] !== selectedStandardReportConfig) {
    t37 = selectedStandardReportConfig != null && REPORT_TEMPLATE_WITH_CONVERSION_EVENTS.includes(selectedStandardReportConfig.templateId);
    $[99] = selectedStandardReportConfig;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  const reportSupportsConversionEvents = t37;
  let t38;
  bb8: {
    if (!hasSuccessfullyLoadedNecessaryData || reportParameters == null) {
      t38 = null;
      break bb8;
    }
    let t39;
    if ($[101] !== reportParameters.transformations || $[102] !== totalsData) {
      t39 = reportParameters.transformations.transformToSummary(totalsData);
      $[101] = reportParameters.transformations;
      $[102] = totalsData;
      $[103] = t39;
    } else {
      t39 = $[103];
    }
    t38 = t39;
  }
  const summaryData = t38;
  let t39;
  bb9: {
    if (!hasSuccessfullyLoadedNecessaryData || reportParameters == null) {
      t39 = null;
      break bb9;
    }
    let t40;
    if ($[104] !== previousTotalsData || $[105] !== reportParameters.transformations) {
      t40 = reportParameters.transformations.transformToSummary(previousTotalsData);
      $[104] = previousTotalsData;
      $[105] = reportParameters.transformations;
      $[106] = t40;
    } else {
      t40 = $[106];
    }
    t39 = t40;
  }
  const summaryDataForPreviousTimePeriod = t39;
  let t40;
  bb10: {
    if (!hasSuccessfullyLoadedNecessaryData || reportParameters == null) {
      let t41;
      if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
        t41 = {};
        $[107] = t41;
      } else {
        t41 = $[107];
      }
      t40 = t41;
      break bb10;
    }
    let t41;
    if ($[108] !== reportParameters.transformations || $[109] !== selectedTopCardMetrics || $[110] !== totalsGroupedByDate) {
      t41 = reportParameters.transformations.transformToTimeSeries(totalsGroupedByDate, selectedTopCardMetrics as Array<ReportingBaseMetric & ChannelGroupingMetric>);
      $[108] = reportParameters.transformations;
      $[109] = selectedTopCardMetrics;
      $[110] = totalsGroupedByDate;
      $[111] = t41;
    } else {
      t41 = $[111];
    }
    t40 = t41;
  }
  const progressOverTimeRecords = t40;
  const progressOverTimeChartQueryState = useHomeProgressOverTimeChartQueryState();
  const detailedTableQueryState = useHomeDetailedTableQueryState(listOfMetricsToShowInDropDownWithBudgetMetrics);
  const {
    registerScorecardElement,
    scorecardMetricElementRefsById
  } = useHomeCopilotScorecardElementRefs(selectedTopCardMetrics);
  let t41;
  if ($[112] !== canFilter || $[113] !== dateRangeCalendarRef || $[114] !== detailedTableQueryState || $[115] !== detailedTableRef || $[116] !== eventOptions || $[117] !== filterBarRef || $[118] !== getConversionNamesAndSources || $[119] !== hasSuccessfullyLoadedNecessaryData || $[120] !== isBudgetMode || $[121] !== isConversionNamesAndSourcesError || $[122] !== isDataError || $[123] !== isFilterOptionsLoaded || $[124] !== isLoadingConversionNamesAndSources || $[125] !== isNoReportDataViewConditionSatisfied || $[126] !== isNoReportViewConditionSatisfied || $[127] !== isReportDataKnownErrorViewConditionSatisfied || $[128] !== isReportDataLoadingViewConditionSatisfied || $[129] !== isReportDataUnknownErrorViewConditionSatisfied || $[130] !== listOfMetricsToShowInDropDownWithBudgetMetrics || $[131] !== moreMenuButtonRef || $[132] !== parameterOptions || $[133] !== periodTypeToApply || $[134] !== previousTimePeriod || $[135] !== progressOverTimeChartQueryState || $[136] !== progressOverTimeChartRef || $[137] !== progressOverTimeRecords || $[138] !== recommendationsRef || $[139] !== reportParameters || $[140] !== reportSupportsConversionEvents || $[141] !== reports || $[142] !== reportsForPreviousTimePeriod || $[143] !== scorecardGroupRef || $[144] !== scorecardMetricElementRefsById || $[145] !== selectedConversionEvents || $[146] !== selectedDate || $[147] !== selectedFilters || $[148] !== selectedStandardReportConfig || $[149] !== selectedTopCardMetrics || $[150] !== setSelectedDate || $[151] !== setSelectedFilters || $[152] !== summaryData || $[153] !== summaryDataForPreviousTimePeriod) {
    t41 = {
      selectedDate,
      setSelectedDate,
      previousTimePeriod,
      selectedStandardReportConfig,
      reportParameters,
      selectedFilters,
      setSelectedFilters,
      parameterOptions,
      canFilter,
      isFilterOptionsLoaded,
      selectedTopCardMetrics,
      setSelectedTopCardMetrics,
      summaryData,
      summaryDataForPreviousTimePeriod,
      progressOverTimeRecords,
      reports,
      reportsForPreviousTimePeriod,
      periodTypeToApply,
      progressOverTimeChartQueryState,
      detailedTableQueryState,
      listOfMetricsToShowInDropDownWithBudgetMetrics,
      reportRowCount: reports.length,
      isBudgetMode,
      reportSupportsConversionEvents,
      eventOptions,
      selectedConversionEvents,
      setSelectedConversionEvents,
      getConversionNamesAndSources,
      isLoadingConversionNamesAndSources,
      isConversionNamesAndSourcesError,
      hasSuccessfullyLoadedNecessaryData,
      isDataError,
      isNoReportViewConditionSatisfied,
      isReportDataLoadingViewConditionSatisfied,
      isReportDataKnownErrorViewConditionSatisfied,
      isReportDataUnknownErrorViewConditionSatisfied,
      isNoReportDataViewConditionSatisfied,
      dateRangeCalendarRef,
      filterBarRef,
      scorecardGroupRef,
      scorecardMetricElementRefsById,
      progressOverTimeChartRef,
      detailedTableRef,
      recommendationsRef,
      moreMenuButtonRef
    };
    $[112] = canFilter;
    $[113] = dateRangeCalendarRef;
    $[114] = detailedTableQueryState;
    $[115] = detailedTableRef;
    $[116] = eventOptions;
    $[117] = filterBarRef;
    $[118] = getConversionNamesAndSources;
    $[119] = hasSuccessfullyLoadedNecessaryData;
    $[120] = isBudgetMode;
    $[121] = isConversionNamesAndSourcesError;
    $[122] = isDataError;
    $[123] = isFilterOptionsLoaded;
    $[124] = isLoadingConversionNamesAndSources;
    $[125] = isNoReportDataViewConditionSatisfied;
    $[126] = isNoReportViewConditionSatisfied;
    $[127] = isReportDataKnownErrorViewConditionSatisfied;
    $[128] = isReportDataLoadingViewConditionSatisfied;
    $[129] = isReportDataUnknownErrorViewConditionSatisfied;
    $[130] = listOfMetricsToShowInDropDownWithBudgetMetrics;
    $[131] = moreMenuButtonRef;
    $[132] = parameterOptions;
    $[133] = periodTypeToApply;
    $[134] = previousTimePeriod;
    $[135] = progressOverTimeChartQueryState;
    $[136] = progressOverTimeChartRef;
    $[137] = progressOverTimeRecords;
    $[138] = recommendationsRef;
    $[139] = reportParameters;
    $[140] = reportSupportsConversionEvents;
    $[141] = reports;
    $[142] = reportsForPreviousTimePeriod;
    $[143] = scorecardGroupRef;
    $[144] = scorecardMetricElementRefsById;
    $[145] = selectedConversionEvents;
    $[146] = selectedDate;
    $[147] = selectedFilters;
    $[148] = selectedStandardReportConfig;
    $[149] = selectedTopCardMetrics;
    $[150] = setSelectedDate;
    $[151] = setSelectedFilters;
    $[152] = summaryData;
    $[153] = summaryDataForPreviousTimePeriod;
    $[154] = t41;
  } else {
    t41 = $[154];
  }
  useHomeCopilotTools(t41);
  let t42;
  if ($[155] !== pushQuery || $[156] !== resetHomePageData || $[157] !== setSelectedFilters) {
    t42 = config_0 => {
      setSelectedStandardReportConfigId(config_0.id.toString());
      pushQuery({
        reportId: config_0.id.toString()
      });
      OverScreen.remove("switchReportHomeFlyout");
      resetHomePageData();
      setSelectedFilters([]);
      setSelectedConversionEvents(undefined);
      setEventOptions(undefined);
    };
    $[155] = pushQuery;
    $[156] = resetHomePageData;
    $[157] = setSelectedFilters;
    $[158] = t42;
  } else {
    t42 = $[158];
  }
  const handleReportSelected = t42;
  let t43;
  if ($[159] !== handleReportSelected || $[160] !== selectedStandardReportConfig?.configId || $[161] !== standardReportConfigsFilteredByTemplateId) {
    t43 = function handleSwitchReports() {
      OverScreen.show("switchReportHomeFlyout", {
        configs: standardReportConfigsFilteredByTemplateId.map(config_1 => ({
          id: config_1.configId,
          name: config_1.descriptiveName,
          subtitle: config_1.configId,
          templateId: config_1.templateId,
          isSelected: config_1.configId.toString() === selectedStandardReportConfig?.configId.toString()
        })),
        onSelected: handleReportSelected
      });
    };
    $[159] = handleReportSelected;
    $[160] = selectedStandardReportConfig?.configId;
    $[161] = standardReportConfigsFilteredByTemplateId;
    $[162] = t43;
  } else {
    t43 = $[162];
  }
  const handleSwitchReports = t43;
  let t44;
  if ($[163] !== listOfMetricsToShowInDropDownWithBudgetMetrics || $[164] !== reportParameters?.localStorageKeyForMetrics || $[165] !== selectedTopCardMetrics || $[166] !== t) {
    t44 = () => {
      OverScreen.show("topCardMetricsFlyout", {
        t,
        metricOptions: listOfMetricsToShowInDropDownWithBudgetMetrics.map(metric_1 => ({
          value: metric_1,
          label: t(metric_1)
        })),
        selectedMetrics: selectedTopCardMetrics,
        setSelectedMetrics: setSelectedTopCardMetrics,
        metricsStorageKey: reportParameters?.localStorageKeyForMetrics
      });
    };
    $[163] = listOfMetricsToShowInDropDownWithBudgetMetrics;
    $[164] = reportParameters?.localStorageKeyForMetrics;
    $[165] = selectedTopCardMetrics;
    $[166] = t;
    $[167] = t44;
  } else {
    t44 = $[167];
  }
  const handleSelectTopCardMetrics = t44;
  let t45;
  if ($[168] !== eventOptions || $[169] !== reportParameters?.conversionEventVariables || $[170] !== reportParameters?.storeSelectedConversionEvents || $[171] !== selectedConversionEvents || $[172] !== selectedDate.endDate || $[173] !== selectedDate.startDate || $[174] !== selectedStandardReportConfig || $[175] !== t) {
    t45 = () => {
      if (selectedStandardReportConfig != null) {
        OverScreen.show("conversionEventsFlyout", {
          t,
          currentEventOptions: eventOptions,
          setCurrentEventOptions: setEventOptions,
          reportId: selectedStandardReportConfig.configId.toString(),
          selectedEvents: selectedConversionEvents ?? [],
          setSelectedEvents: setSelectedConversionEvents,
          startDate: selectedDate.startDate,
          endDate: selectedDate.endDate,
          reportType: selectedStandardReportConfig.templateId,
          conversionEventVariables: reportParameters?.conversionEventVariables,
          useConversionData: useHomePageData,
          conversionSourceToDatasourceMap: CONVERSION_SOURCE_TO_DATASOURCE_MAP,
          onSave: reportParameters?.storeSelectedConversionEvents
        });
      }
    };
    $[168] = eventOptions;
    $[169] = reportParameters?.conversionEventVariables;
    $[170] = reportParameters?.storeSelectedConversionEvents;
    $[171] = selectedConversionEvents;
    $[172] = selectedDate.endDate;
    $[173] = selectedDate.startDate;
    $[174] = selectedStandardReportConfig;
    $[175] = t;
    $[176] = t45;
  } else {
    t45 = $[176];
  }
  const handleSelectConversionEvents = t45;
  let t46;
  if ($[177] !== isBudgetMode || $[178] !== isHomePageDataLoaded || $[179] !== reportParameters || $[180] !== reports || $[181] !== selectedStandardReportConfig || $[182] !== t) {
    t46 = () => {
      const handleChangeBudgetMode = isBudgetMode_0 => {
        if (selectedStandardReportConfig?.configId.toString() == null) {
          return;
        }
        reportParameters?.setLocalStorageIsBudgetMode(selectedStandardReportConfig.configId.toString(), isBudgetMode_0);
        setIsBudgetMode(isBudgetMode_0);
      };
      OverScreen.show("budgetAndTargetsFlyout", {
        t,
        showBudgetsAndTargets: isBudgetMode,
        setShowBudgetsAndTargets: handleChangeBudgetMode,
        loading: !isHomePageDataLoaded,
        reports: reports as ReportingBaseReport[]
      });
    };
    $[177] = isBudgetMode;
    $[178] = isHomePageDataLoaded;
    $[179] = reportParameters;
    $[180] = reports;
    $[181] = selectedStandardReportConfig;
    $[182] = t;
    $[183] = t46;
  } else {
    t46 = $[183];
  }
  const handleSelectBudgetAndTargets = t46;
  let t47;
  if ($[184] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = getPeriodOfTimeToday();
    $[184] = t47;
  } else {
    t47 = $[184];
  }
  let t48;
  if ($[185] !== t) {
    t48 = t(`greetings.${t47}`);
    $[185] = t;
    $[186] = t48;
  } else {
    t48 = $[186];
  }
  const greetingKey = t48;
  let t49;
  if ($[187] !== setSelectedDate) {
    t49 = (startDate, endDate) => {
      setSelectedDate({
        startDate: startDate ?? defaultStartDate,
        endDate: endDate ?? defaultEndDate
      });
    };
    $[187] = setSelectedDate;
    $[188] = t49;
  } else {
    t49 = $[188];
  }
  const changeDateRange = t49;
  let t50;
  if ($[189] !== selectedStandardReportConfig?.configId) {
    t50 = (startDate_0, endDate_0) => {
      setCustomComparisonRange({
        startDate: startDate_0,
        endDate: endDate_0
      });
      persistHomeCustomComparisonRange(selectedStandardReportConfig?.configId.toString(), userSelectedPeriodTypeRef.current, {
        startDate: startDate_0,
        endDate: endDate_0
      });
    };
    $[189] = selectedStandardReportConfig?.configId;
    $[190] = t50;
  } else {
    t50 = $[190];
  }
  const changeComparisonRange = t50;
  let t51;
  if ($[191] !== selectedStandardReportConfig?.configId) {
    t51 = () => {
      setPeriodTypeToApply(userSelectedPeriodTypeRef.current);
      persistHomeComparisonPeriodSelection(selectedStandardReportConfig?.configId.toString(), userSelectedPeriodTypeRef.current);
    };
    $[191] = selectedStandardReportConfig?.configId;
    $[192] = t51;
  } else {
    t51 = $[192];
  }
  const handleApplyDateRangeClick = t51;
  let t52;
  if ($[193] !== isCustomContentConditionSatisfied || $[194] !== isNoReportDataViewConditionSatisfied || $[195] !== isNoReportViewConditionSatisfied || $[196] !== isNonErrorViewConditionSatisfied || $[197] !== isReportDataKnownErrorViewConditionSatisfied || $[198] !== isReportDataLoadingViewConditionSatisfied || $[199] !== isReportDataUnknownErrorViewConditionSatisfied || $[200] !== selectedStandardReportConfig || $[201] !== selectedTopCardMetrics || $[202] !== t) {
    t52 = isCustomContentConditionSatisfied ? <>{isNonErrorViewConditionSatisfied && <StyledBlockEmptyStateContainer><Typography variant="h3">{t("performanceData")}</Typography>{isNoReportViewConditionSatisfied && <NoReport />}{isReportDataLoadingViewConditionSatisfied && <ReportDataLoading t={t} />}{isNoReportDataViewConditionSatisfied && <NoData title={t("noDataMatchingYourSelection")} body={t("noDataMatchingYourSelectionPrompt")} />}</StyledBlockEmptyStateContainer>}{isReportDataKnownErrorViewConditionSatisfied && <ReportDataError selectedMetrics={selectedTopCardMetrics} report={selectedStandardReportConfig} errorType="status" />}{isReportDataUnknownErrorViewConditionSatisfied && <ReportDataError selectedMetrics={selectedTopCardMetrics} errorType="unknown" />}</> : null;
    $[193] = isCustomContentConditionSatisfied;
    $[194] = isNoReportDataViewConditionSatisfied;
    $[195] = isNoReportViewConditionSatisfied;
    $[196] = isNonErrorViewConditionSatisfied;
    $[197] = isReportDataKnownErrorViewConditionSatisfied;
    $[198] = isReportDataLoadingViewConditionSatisfied;
    $[199] = isReportDataUnknownErrorViewConditionSatisfied;
    $[200] = selectedStandardReportConfig;
    $[201] = selectedTopCardMetrics;
    $[202] = t;
    $[203] = t52;
  } else {
    t52 = $[203];
  }
  const customContentBasedOnDataOrError = t52;
  let t53;
  if ($[204] !== canFilter || $[205] !== changeComparisonRange || $[206] !== changeDateRange || $[207] !== customComparisonRange || $[208] !== dateRangeCalendarRef || $[209] !== eventOptions?.length || $[210] !== filterBarRef || $[211] !== handleApplyDateRangeClick || $[212] !== handleSelectBudgetAndTargets || $[213] !== handleSelectConversionEvents || $[214] !== handleSelectTopCardMetrics || $[215] !== handleSwitchReports || $[216] !== isConversionNamesAndSourcesError || $[217] !== isNoReportViewConditionSatisfied || $[218] !== moreMenuButtonRef || $[219] !== parameterOptions || $[220] !== selectedConversionEvents?.length || $[221] !== selectedDate || $[222] !== selectedFilters || $[223] !== selectedReportErrorCode || $[224] !== selectedReportErrorDetails || $[225] !== selectedReportStatus || $[226] !== selectedReportStatusLastUpdated || $[227] !== selectedReportStatusMessage || $[228] !== selectedStandardReportConfig || $[229] !== selectedTopCardMetrics.length || $[230] !== setSelectedFilters || $[231] !== standardReportConfigsFilteredByTemplateId || $[232] !== userSelectedPeriodType) {
    t53 = !isNoReportViewConditionSatisfied && <Toolbar selectedStandardReportConfig={selectedStandardReportConfig} statusInfo={{
      selectedReportStatus,
      selectedReportErrorCode,
      selectedReportErrorDetails,
      selectedReportStatusLastUpdated,
      selectedReportStatusMessage
    }} filterProps={{
      canFilter,
      selectedFilters,
      setSelectedFilters,
      parameterOptions,
      selectedDate,
      changeDateRange,
      changeComparisonRange,
      customComparisonRange,
      handleApplyDateRangeClick,
      setUserSelectedPeriodType: changeUserSelectedPeriodType,
      userSelectedPeriodType,
      dateRangeCalendarRef,
      filterBarRef
    }} moreMenuProps={{
      canSwitchReports: standardReportConfigsFilteredByTemplateId.length > 1,
      handleSwitchReports,
      disableConversionEvents: eventOptions?.length === 0 || isConversionNamesAndSourcesError,
      handleSelectTopCardMetrics,
      handleSelectConversionEvents,
      topCardMetricsCount: selectedTopCardMetrics.length,
      conversionEventsCount: selectedConversionEvents?.length,
      handleSelectBudgetAndTargets,
      moreMenuButtonRef
    }} />;
    $[204] = canFilter;
    $[205] = changeComparisonRange;
    $[206] = changeDateRange;
    $[207] = customComparisonRange;
    $[208] = dateRangeCalendarRef;
    $[209] = eventOptions?.length;
    $[210] = filterBarRef;
    $[211] = handleApplyDateRangeClick;
    $[212] = handleSelectBudgetAndTargets;
    $[213] = handleSelectConversionEvents;
    $[214] = handleSelectTopCardMetrics;
    $[215] = handleSwitchReports;
    $[216] = isConversionNamesAndSourcesError;
    $[217] = isNoReportViewConditionSatisfied;
    $[218] = moreMenuButtonRef;
    $[219] = parameterOptions;
    $[220] = selectedConversionEvents?.length;
    $[221] = selectedDate;
    $[222] = selectedFilters;
    $[223] = selectedReportErrorCode;
    $[224] = selectedReportErrorDetails;
    $[225] = selectedReportStatus;
    $[226] = selectedReportStatusLastUpdated;
    $[227] = selectedReportStatusMessage;
    $[228] = selectedStandardReportConfig;
    $[229] = selectedTopCardMetrics.length;
    $[230] = setSelectedFilters;
    $[231] = standardReportConfigsFilteredByTemplateId;
    $[232] = userSelectedPeriodType;
    $[233] = t53;
  } else {
    t53 = $[233];
  }
  let t54;
  if ($[234] !== t53) {
    t54 = <>{t53}</>;
    $[234] = t53;
    $[235] = t54;
  } else {
    t54 = $[235];
  }
  const toolbar = t54;
  let t55;
  if ($[236] !== detailedTableQueryState || $[237] !== detailedTableRef || $[238] !== hasSuccessfullyLoadedNecessaryData || $[239] !== listOfMetricsToShowInDropDownWithBudgetMetrics || $[240] !== previousTimePeriod || $[241] !== previousTotalsData || $[242] !== reportParameters || $[243] !== reports || $[244] !== reportsForPreviousTimePeriod || $[245] !== selectedDate.endDate || $[246] !== selectedDate.startDate || $[247] !== totalsData) {
    t55 = hasSuccessfullyLoadedNecessaryData && reportParameters != null ? <DetailedReport ref={detailedTableRef} reports={reports} summaryData={reportParameters.transformations.transformToSummary(totalsData)} timePeriod={[selectedDate.startDate, selectedDate.endDate]} metrics={listOfMetricsToShowInDropDownWithBudgetMetrics} metricToPreviousMetricMap={reportParameters.metricsToPreviousMetricsMap} previousTimePeriod={previousTimePeriod} reportsForPreviousTimePeriod={reportsForPreviousTimePeriod} transformToDetailedReportData={reportParameters.transformations.transformToDetailedReportData as TransformToDetailedDataFn<Report>} summaryDataForPreviousTimePeriod={reportParameters.transformations.transformToSummary(previousTotalsData)} tableQueryState={detailedTableQueryState} /> : null;
    $[236] = detailedTableQueryState;
    $[237] = detailedTableRef;
    $[238] = hasSuccessfullyLoadedNecessaryData;
    $[239] = listOfMetricsToShowInDropDownWithBudgetMetrics;
    $[240] = previousTimePeriod;
    $[241] = previousTotalsData;
    $[242] = reportParameters;
    $[243] = reports;
    $[244] = reportsForPreviousTimePeriod;
    $[245] = selectedDate.endDate;
    $[246] = selectedDate.startDate;
    $[247] = totalsData;
    $[248] = t55;
  } else {
    t55 = $[248];
  }
  const detailedReport = t55;
  const t56 = selectedTopCardMetrics.length;
  let t57;
  if ($[249] !== theme) {
    t57 = theme.spacing(8);
    $[249] = theme;
    $[250] = t57;
  } else {
    t57 = $[250];
  }
  let t58;
  if ($[251] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <NativePinterestBanner bannerType="home_page" />;
    $[251] = t58;
  } else {
    t58 = $[251];
  }
  let t59;
  if ($[252] !== currentClient.id || $[253] !== isReportDataKnownErrorViewConditionSatisfied || $[254] !== selectedReportStatus || $[255] !== selectedStandardReportConfig || $[256] !== t) {
    t59 = selectedReportStatus != null && STATUSES_TO_SHOW_BANNER_FOR.includes(selectedReportStatus) && !isReportDataKnownErrorViewConditionSatisfied && selectedStandardReportConfig != null && <Banner learnMoreButton={{
      children: t("buttons.learnMore"),
      href: `${APP_ROUTES.reportingSolutions.editConfigPage({
        configId: selectedStandardReportConfig.configId.toString()
      })}?clientId=${currentClient.id}`,
      target: "_blank"
    }} variant="warning" content={selectedReportStatus === STATUSES.paused ? t("configPausedNotice") : t("configErrorNotice")} />;
    $[252] = currentClient.id;
    $[253] = isReportDataKnownErrorViewConditionSatisfied;
    $[254] = selectedReportStatus;
    $[255] = selectedStandardReportConfig;
    $[256] = t;
    $[257] = t59;
  } else {
    t59 = $[257];
  }
  const t60 = currentUser?.name;
  let t61;
  if ($[258] !== greetingKey || $[259] !== t60) {
    t61 = <Typography variant="h1">{greetingKey}, {t60}</Typography>;
    $[258] = greetingKey;
    $[259] = t60;
    $[260] = t61;
  } else {
    t61 = $[260];
  }
  let t62;
  if ($[261] !== t57 || $[262] !== t59 || $[263] !== t61) {
    t62 = <Grid container={true} marginBottom={t57} rowGap="24px" direction="column">{t58}{t59}{t61}</Grid>;
    $[261] = t57;
    $[262] = t59;
    $[263] = t61;
    $[264] = t62;
  } else {
    t62 = $[264];
  }
  const t63 = isToolbarConditionSatisfied ? toolbar : null;
  let t64;
  if ($[265] !== customContentBasedOnDataOrError || $[266] !== hasSuccessfullyLoadedNecessaryData || $[267] !== isBudgetMode || $[268] !== periodTypeToApply || $[269] !== previousTimePeriod || $[270] !== progressOverTimeChartQueryState || $[271] !== progressOverTimeChartRef || $[272] !== progressOverTimeRecords || $[273] !== registerScorecardElement || $[274] !== reportParameters || $[275] !== reports || $[276] !== reportsForPreviousTimePeriod || $[277] !== scorecardGroupRef || $[278] !== selectedDate.endDate || $[279] !== selectedDate.startDate || $[280] !== summaryData || $[281] !== summaryDataForPreviousTimePeriod) {
    t64 = customContentBasedOnDataOrError ?? (hasSuccessfullyLoadedNecessaryData && reportParameters != null ? <><Summary startDate={selectedDate.startDate} endDate={selectedDate.endDate} summaryData={summaryData} summaryDataForPreviousTimePeriod={summaryDataForPreviousTimePeriod} progressOverTimeRecords={progressOverTimeRecords} previousTimePeriod={previousTimePeriod} scorecardGroupRef={scorecardGroupRef} registerScorecardElement={registerScorecardElement} /><ProgressOverTime ref={progressOverTimeChartRef} isBudgetMode={isBudgetMode} reports={reports} metrics={reportParameters?.metrics} reportsForPreviousTimePeriod={reportsForPreviousTimePeriod} timePeriod={[selectedDate.startDate, selectedDate.endDate]} selectedPeriodType={periodTypeToApply} chartQueryState={progressOverTimeChartQueryState} transformToTimeSeriesByMetricAndBreakdown={reportParameters.transformations.transformToTimeSeriesByMetricAndBreakdown as TransformToTimeSeriesByMetricAndBreakdownFn<Report, Metric>} /></> : null);
    $[265] = customContentBasedOnDataOrError;
    $[266] = hasSuccessfullyLoadedNecessaryData;
    $[267] = isBudgetMode;
    $[268] = periodTypeToApply;
    $[269] = previousTimePeriod;
    $[270] = progressOverTimeChartQueryState;
    $[271] = progressOverTimeChartRef;
    $[272] = progressOverTimeRecords;
    $[273] = registerScorecardElement;
    $[274] = reportParameters;
    $[275] = reports;
    $[276] = reportsForPreviousTimePeriod;
    $[277] = scorecardGroupRef;
    $[278] = selectedDate.endDate;
    $[279] = selectedDate.startDate;
    $[280] = summaryData;
    $[281] = summaryDataForPreviousTimePeriod;
    $[282] = t64;
  } else {
    t64 = $[282];
  }
  let t65;
  if ($[283] !== detailedReport || $[284] !== isCustomContentConditionSatisfied) {
    t65 = isCustomContentConditionSatisfied ? <></> : detailedReport;
    $[283] = detailedReport;
    $[284] = isCustomContentConditionSatisfied;
    $[285] = t65;
  } else {
    t65 = $[285];
  }
  let t66;
  if ($[286] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <StyledRecommendationsBlock><RecommendationsV2 /></StyledRecommendationsBlock>;
    $[286] = t66;
  } else {
    t66 = $[286];
  }
  let t67;
  if ($[287] !== recommendationsRef || $[288] !== selectedTopCardMetrics.length || $[289] !== t62 || $[290] !== t63 || $[291] !== t64 || $[292] !== t65) {
    t67 = <Grid><Layout summaryCardsCount={t56} header={t62} toolbar={t63} content={t64} additionalContent={t65} rightAlignedContent={t66} rightAlignedContentRef={recommendationsRef} /></Grid>;
    $[287] = recommendationsRef;
    $[288] = selectedTopCardMetrics.length;
    $[289] = t62;
    $[290] = t63;
    $[291] = t64;
    $[292] = t65;
    $[293] = t67;
  } else {
    t67 = $[293];
  }
  return t67;
};
const StyledRecommendationsBlock = styled(Block)(({
  theme
}) => ({
  height: '100%',
  overflowY: 'auto',
  overflowX: 'clip',
  justifyContent: 'flex-start',
  button: {
    alignSelf: 'flex-start'
  }
}));
const StyledBlockEmptyStateContainer = styled(Block)(({
  theme
}) => ({
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: 'calc(100% - 16px)',
  gap: '24px',
  '& > h3': {
    alignSelf: 'flex-start'
  }
}));
export default Dashboard;
function _temp(config) {
  return objectKeys(reportsTypeParametersMap).includes(config.templateId);
}
function _temp2(conversionNameAndSource) {
  return {
    label: conversionNameAndSource.conversionName,
    value: conversionNameAndSource.conversionName.toString(),
    subCategory: conversionNameAndSource.conversionSource,
    numberOfConversions: conversionNameAndSource.sumConversions
  };
}
function _temp3(market) {
  return {
    value: market.id,
    label: market.name
  };
}
function _temp4(channel) {
  return {
    value: channel.id,
    label: channel.name
  };
}
function _temp5(metric_0) {
  return !isBudgetMetric(metric_0);
}